import { Spinner, Trash } from '@medusajs/icons';
import { useCallback } from 'react';
import { toast } from '@medusajs/ui';

import { AppLink } from '@adapters';
import {
  useUpdateLineItem,
  useDeleteLineItem,
} from '@lib/query/mutations/cart';
import TextLink from '@modules/common/components/text-link';
import { isCashOnlyProductVariant } from '@lib/util/is-cash-only-product-variant';
import type { EnrichedLineItem } from '@marxbiotech/shared-types/medusa';
import Counter from '@modules/common/components/counter';
import LineItemPrice from '@modules/common/components/line-item-price';
import Thumbnail from '@modules/products/components/thumbnail';

interface CartItemProps {
  item: EnrichedLineItem;
  onClose: () => void;
}

/**
 * Individual cart item in the drawer
 * Displays product info, quantity controls, and delete button
 */
export default function CartItem({ item, onClose }: CartItemProps) {
  const { mutate: updateLineItem, isPending: isUpdating } = useUpdateLineItem({
    onError: () => {
      toast.error('更新數量失敗，請稍後再試');
    },
  });
  const { mutate: deleteLineItem, isPending: isDeleting } = useDeleteLineItem({
    onError: () => {
      toast.error('刪除商品失敗，請稍後再試');
    },
  });

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      updateLineItem({ lineItemId: item.id, quantity: newQuantity });
    },
    [item.id, updateLineItem]
  );

  const handleDelete = useCallback(() => {
    deleteLineItem(item.id);
  }, [deleteLineItem, item.id]);

  const productHandle = item.variant?.product?.handle;
  const thumbnail = item.variant?.product?.thumbnail;
  const maxQuantity = item.variant?.inventory_quantity ?? undefined;
  const isCashOnly = isCashOnlyProductVariant(item.variant);

  return (
    <div className="flex gap-6 py-4" data-testid={`cart-item-${item.id}`}>
      {/* Product thumbnail */}
      <AppLink
        href={`/products/${productHandle}`}
        onClick={onClose}
        className="basis-1/4"
      >
        <Thumbnail
          thumbnail={thumbnail}
          size="square-lg"
          data-testid="cart-item-thumbnail"
        />
      </AppLink>

      {/* Product info */}
      <div className="flex basis-3/4 flex-col justify-between">
        <div>
          <TextLink
            variant="title-regular"
            href={`/products/${productHandle}`}
            onClick={onClose}
            className="text-black-30 w-full"
            data-testid={`cart-item-title-${item.id}`}
          >
            {item.product_title}
          </TextLink>
          <LineItemPrice item={item} isCashOnly={isCashOnly} />
        </div>

        {/* Quantity controls */}
        <div className="flex items-center gap-4">
          <Counter
            count={item.quantity}
            max={maxQuantity}
            disabled={isUpdating}
            delay={500}
            onChange={handleQuantityChange}
            className="flex-auto"
            data-testid={`cart-item-counter-${item.id}`}
          />

          {/* Delete button */}
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="group p-4"
            data-testid={`cart-item-delete-${item.id}`}
          >
            {isDeleting ? (
              <Spinner className="animate-spin" />
            ) : (
              <Trash className="group-hover:text-red-20" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
